/**
 * `interactive.*` — the FILLS a control paints on itself.
 *
 * Split out of `semanticTokens.ts` when that file crossed the 1000-line CI
 * ceiling. This is the family that keeps growing: it started as one neutral
 * resting/hover/active ladder and now carries a ladder per INTENT, each with
 * the measurement that justifies its steps. Those explanations are the bulk of
 * the lines here and they are the point — the warning ladder in particular runs
 * the opposite way to every other one, and that is not guessable from the code.
 *
 * WHAT BELONGS HERE: a token a control uses as its own background. What does
 * NOT: the tints a control paints on a SURFACE (`bg.error`, `bg.success.active`
 * and friends stay with the other backgrounds), and the inks (`text.onBrand`,
 * `text.onWarning`), which are chosen to pair with a fill but are text.
 *
 * Every ladder here is pinned in `__tests__/componentColorSchemeResolution.test.ts`:
 * three distinct steps, and the label legible on each one.
 */

import { DARK_DERIVED } from './colors.brand';

export const interactiveTokens = {
  'interactive.default': {
    default: 'neutral.200',
    _dark: DARK_DERIVED.surface,
  },
  'interactive.hover': {
    default: 'neutral.300',
    _dark: DARK_DERIVED.chromeActive,
  },
  'interactive.active': {
    default: 'neutral.400',
    _dark: DARK_DERIVED.border,
  },
  // The FILL of a destructive CTA — "Delete Forever", "Delete my account".
  //
  // Sits beside `brand.primary` rather than in the `danger.*` ramp, because
  // this is a ROLE (the filled destructive control) and the ramp is a
  // palette. Dotted states, like `brand.secondary` above, and THREE distinct
  // steps per the ladder rule at the top of this file — 600 / 700 / 800
  // carry white at 5.57 / 7.71 / 10.83, all past 1.4.3. `danger.500` was
  // rejected for the resting fill: white on it is 3.94:1 and fails.
  //
  // NO `_dark` PAIR, and the reason is a palette limit, not an analogy.
  // An earlier draft justified it as "matching `brand.primary`", which was
  // wrong twice over: the property it cited (the white label) is
  // mode-invariant here and so can never distinguish the two tokens, and the
  // property that DOES differ was never measured. Measured against the dark
  // panel, #202520 since the 2026-09-22 lift: this fill is 2.80:1 where
  // `brand.primary` is 3.27:1. (On the pre-lift #1C211C they were 2.94 and
  // 3.43 — the lift costs every dark ink about 5% and changes neither verdict,
  // but `brand.primary`'s margin over 3:1 is now 0.27, not 0.43.)
  // So it sits under 1.4.11's 3:1 for a non-text boundary and the brand CTA
  // does not.
  //
  // It stays anyway, because the ramp cannot do better. Clearing 3:1 against
  // the dark panel needs `danger.500` or lighter; carrying a white label at
  // 4.5:1 needs `danger.600` or darker. No step satisfies both, and 1.4.3 on
  // the label is the binding one — 1.4.11 exempts a boundary whose control is
  // identified by its own visible text, which every one of these buttons is
  // ("Delete Forever", "Revoke all"). Asserted for all three steps in
  // `componentColorSchemeResolution.test.ts` so a palette change surfaces
  // here rather than being discovered on screen.
  'interactive.danger': {
    default: 'danger.600',
  },
  'interactive.danger.hover': {
    default: 'danger.700',
  },
  'interactive.danger.active': {
    default: 'danger.800',
  },

  // The FILL of a POSITIVE confirming action — "Enable", "Copied".
  //
  // Same shape as `interactive.danger` (600 / 700 / 800) and the same
  // no-`_dark` reasoning. White clears 1.4.3 on all three steps
  // (4.98 / 7.26 / 10.42).
  //
  // On the FILL-vs-SURFACE leg, measured against `bg.elevated` — the surface a
  // Modal and an AlertDialog actually set, and where the one success call site
  // lives — it is 4.98:1 in light and 2.94:1 in dark, against danger's 5.57 and
  // 2.63. Both clear 1.4.11 comfortably in light; both sit under it in dark,
  // success by less. An earlier note quoted "3.28 vs 2.94", which is the
  // `frame.panel` pair (3.13 vs 2.80 since the 2026-09-22 panel lift): true of
  // a surface these buttons do not render on, and
  // it flattered the comparison by measuring the two tokens against different
  // grounds. Same 1.4.11 exemption danger carries — the control is identified
  // by its own visible text.
  //
  // `success.500` was rejected for the resting fill — white on it is 3.10:1.
  'interactive.success': {
    default: 'success.600',
  },
  'interactive.success.hover': {
    default: 'success.700',
  },
  'interactive.success.active': {
    default: 'success.800',
  },

  // The FILL of a CAUTIONING action — "Move to Trash". Reversible, so not
  // `danger`; consequential, so not the brand CTA.
  //
  // THE RAMP RUNS THE OTHER WAY and that decides everything here. Warning is
  // a LIGHT ramp: white on it is 1.83 at `.500` and still only 3.85 at
  // `.700`, so a white-inked warning button cannot reach 1.4.3 anywhere it
  // still looks amber. It takes a fixed DARK ink instead (`text.onWarning`),
  // which is the same move `secondary` already makes against its grey fill.
  //
  // That inverts the ladder: with a dark ink, DEEPER means LESS contrast, so
  // the three steps run 400 -> 500 -> 600 (9.61 / 8.34 / 5.88) rather than
  // 600 -> 700 -> 800. `warning.700` is the first step that fails (3.96), and
  // it is why the resting fill is the palest of the three rather than the
  // middle one — the ramp offers exactly three qualifying steps and this
  // takes all of them.
  //
  // THE COST, measured and accepted rather than left unsaid. A pale fill is a
  // quiet one: against `bg.elevated` (what a Modal and an AlertDialog set) this
  // is 1.59:1 in light, where `brand.primary` — what these three buttons
  // rendered as before — is 4.77:1 and `interactive.danger` is 5.57:1. In dark
  // it inverts to 9.24:1, because the fill does not move and the surface does.
  //
  // It stands, for the same reason `secondary` ships at 1.28:1: 1.4.11 exempts
  // a boundary whose control is identified by its own visible text, and the
  // label here is 9.61:1. But it IS a weakening in the default colour mode on
  // the sole button in a delete dialog's footer, so it is written down and
  // asserted rather than discovered. If a designer wants the confirm louder,
  // the lever is the ramp, not this token: every step dark enough to raise it
  // costs the label its AA.
  'interactive.warning': {
    default: 'warning.400',
  },
  'interactive.warning.hover': {
    default: 'warning.500',
  },
  'interactive.warning.active': {
    default: 'warning.600',
  },
  'interactive.disabled': {
    default: 'neutral.200',
    _dark: DARK_DERIVED.surface,
  },
  // A control busy on the person's behalf (the note top bar's Voice to text /
  // Image to text while uploading, reading or transcribing). A STATE, not a
  // ladder: it does not respond to hover. One step more filled than
  // `brand.muted`, which is accent.50 and all but invisible on the white
  // top-bar panel ("it isn't very clear when its processing", David
  // 2026-10-29). Ink is `brand.onSurface`, pinned in contrast.test.ts.
  'interactive.busy': {
    default: 'accent.100',
    _dark: 'rgba(92, 136, 218, 0.30)',
  },
  'interactive.busy.border': {
    default: 'accent.200',
    _dark: 'rgba(92, 136, 218, 0.50)',
  },
};
